<script setup>
// Pull to refresh on a phone (docs/design/SPEC.md, «Обновление данных»): pulled down from the very top
// by 64 px (with resistance 0.5) and released → "refresh". Touch screens only; no library.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { t } from '../i18n';

const props = defineProps({
  busy: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['refresh']);

const THRESHOLD = 64;
const RESISTANCE = 0.5;
const MAX = 96;

const distance = ref(0);
let startY = null;

const ready = computed(() => distance.value >= THRESHOLD);

function onStart(event) {
  // A gesture on an open sheet or dialog belongs to it (swipe down closes a sheet), not to the page under it.
  const onDialog = Boolean(event.target?.closest?.('[role="dialog"]'));
  startY = window.scrollY <= 0 && !props.busy && !onDialog ? event.touches[0].clientY : null;
}

function onMove(event) {
  if (startY === null) {
    return;
  }
  const dy = event.touches[0].clientY - startY;
  if (dy <= 0 || window.scrollY > 0) {
    distance.value = 0;
    return;
  }
  distance.value = Math.min(MAX, dy * RESISTANCE);
}

function onEnd() {
  if (startY !== null && ready.value && !props.busy) {
    emit('refresh');
  }
  startY = null;
  distance.value = 0;
}

const enabled = window.matchMedia('(pointer: coarse)').matches;

onMounted(() => {
  if (!enabled) {
    return;
  }
  window.addEventListener('touchstart', onStart, { passive: true });
  window.addEventListener('touchmove', onMove, { passive: true });
  window.addEventListener('touchend', onEnd, { passive: true });
  window.addEventListener('touchcancel', onEnd, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('touchstart', onStart);
  window.removeEventListener('touchmove', onMove);
  window.removeEventListener('touchend', onEnd);
  window.removeEventListener('touchcancel', onEnd);
});
</script>

<template>
  <div>
    <div
      v-if="distance > 0 || busy"
      class="flex items-center justify-center gap-2 overflow-hidden text-[0.8125rem] text-slate-400"
      :style="busy ? { height: '40px' } : { height: `${distance}px` }"
      role="status"
    >
      <svg
        v-if="busy"
        class="size-5 motion-safe:animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          stroke-opacity="0.25"
          stroke-width="2.5"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
        />
      </svg>
      <svg
        v-else
        class="size-5 transition-transform motion-reduce:transition-none"
        :class="ready ? 'rotate-180' : ''"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M12 5v14m-6-6 6 6 6-6" />
      </svg>
      <span>{{ busy ? t('refresh.running') : ready ? t('refresh.release') : t('refresh.pull') }}</span>
    </div>
    <slot />
  </div>
</template>
